import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { type AdminApproval, AdminApprovalType } from '@column/api';
import { Button, Dropdown, Icon, Tooltip } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnDef } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Empty as TableEmpty, tableDefaults } from '~/components/Table';
import { useDeleteCustomRole, useListRoles } from '~/hooks/useRoles';
import {
  AdminApprovalReviewDialog,
  PendingApprovalStatus,
  ReviewChangesButton,
  useAdminApprovalReview,
  usePendingAdminApprovals,
} from '~/lib/approvals';
import { useNavigate } from '~/lib/navigation';
import { precannedRolesByName } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ParagraphLight, TinyHeadline } from '~/styles';

const MANAGE_ROLES_PERMISSION_TOOLTIP = 'You do not have permission to manage roles.';

interface TableRow {
  roleId?: string;
  name: {
    name?: string;
    description?: string | null;
  };
  userCount?: number;
  editable?: boolean;
  pendingApproval?: AdminApproval;
}

const TextWrapper = styled.div`
  display: flex;
  align-items: start;
  vertical-align: middle;
  flex-direction: column;
  min-width: 0;
  max-width: 450px;
  overflow-wrap: anywhere;
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: end;
`;

const LockIcon = styled(Icon.Lock)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

export const PageRoles: React.FC = () => {
  const navigate = useNavigate();
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const canWriteRoles = isAuthorized({ permission: 'users', permissionLevel: 'write' });
  const handleCreateRole = useCallback(() => {
    navigate(ROUTE.PLATFORM_ROLES_CREATE);
  }, [navigate]);

  const { response: rolesResponse, isInitialLoad, isLoading, createRequest: fetchRoles } = useListRoles();
  const pendingRoleUpdates = usePendingAdminApprovals(AdminApprovalType.ROLE_UPDATE);
  const pendingByRole = useMemo(
    () => new Map(pendingRoleUpdates.map((approval) => [approval.request.role_update?.role_id, approval])),
    [pendingRoleUpdates]
  );
  const { openReview, getApprovalActions, startReview, dialogProps } = useAdminApprovalReview(pendingRoleUpdates);
  const deleteRole = useDeleteCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role deleted successfully' });
      fetchRoles();
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const roles = useMemo(() => {
    return (rolesResponse?.roles || []).map((role) => ({
      roleId: role.id,
      name: {
        name: role.editable ? role.name : precannedRolesByName[role.name].label,
        description: role.description,
      },
      userCount: role.userCount,
      editable: role.editable,
      pendingApproval: pendingByRole.get(role.id),
    }));
  }, [rolesResponse, pendingByRole]);

  const handleDelete = useCallback(
    (row: TableRow) => {
      if (!row.roleId) {
        return;
      }
      const roleId = row.roleId;
      openAlert({
        headline: 'Delete role',
        content: 'Are you sure you want to delete this role? This action cannot be reversed.',
        confirmButton: {
          label: 'Delete Role',
          variant: 'danger',
        },
        onSuccess: () => {
          deleteRole.createRequest({ roleId });
        },
      });
    },
    [deleteRole, openAlert]
  );

  const columns: DataTableColumnDef<TableRow>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessor: 'name',
        width: 'minmax(240px, 1fr)',
        className: 'min-w-0 whitespace-normal py-3',
        cell: ({ value }) => (
          <TextWrapper>
            <TinyHeadline bold>{value.name ?? 'Unnamed'}</TinyHeadline>
            {value.description && <ParagraphLight size={'small'}>{value.description}</ParagraphLight>}
          </TextWrapper>
        ),
      },
      {
        header: '',
        accessor: 'pendingApproval',
        width: 'minmax(180px, 25%)',
        cell: ({ value }) =>
          value ? (
            <DataTable.Cell>
              <PendingApprovalStatus />
            </DataTable.Cell>
          ) : null,
      },
      {
        header: 'Users',
        accessor: 'userCount',
        width: 'minmax(min-content, 25%)',
        cell: ({ value }) => (value === 1 ? '1 User' : `${value ?? 0} Users`),
      },
      {
        header: '',
        accessor: 'editable',
        width: 'min-content',
        className: 'justify-end',
        cell: ({ row, value }) => (
          <ActionWrapper onClick={(event) => event.stopPropagation()}>
            {value ? (
              <Dropdown
                isInside
                positionRight
                variant="dots"
                options={[
                  ...(row.pendingApproval ? getApprovalActions(row.pendingApproval) : []),
                  {
                    label: canWriteRoles ? 'Edit' : 'View',
                    onClick: () => navigate(`${ROUTE.PLATFORM_ROLES}/${row.roleId}`),
                  },
                  {
                    label: 'Delete',
                    isDanger: true,
                    isDisabled: !canWriteRoles || (row.userCount ?? 0) > 0,
                    tooltip: !canWriteRoles
                      ? { content: MANAGE_ROLES_PERMISSION_TOOLTIP, placement: 'left' }
                      : (row.userCount ?? 0) > 0
                        ? {
                            content:
                              'This role is currently assigned to users. It cannot be deleted until it is unassigned.',
                            placement: 'left',
                          }
                        : undefined,
                    onClick: () => handleDelete(row),
                  },
                ]}
              />
            ) : (
              <Tooltip content={'Column-managed roles cannot be edited.'} placement={'left'}>
                <LockIcon />
              </Tooltip>
            )}
          </ActionWrapper>
        ),
      },
    ],
    [canWriteRoles, getApprovalActions, handleDelete, navigate]
  );

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} onReviewed={fetchRoles} />
      <PageActions>
        <ReviewChangesButton onClick={startReview} />
        <Tooltip content={MANAGE_ROLES_PERMISSION_TOOLTIP} isOpen={canWriteRoles ? false : undefined} placement="left">
          <Button
            onClick={handleCreateRole}
            icon={<Icon.Plus />}
            size={'small'}
            variant="primary"
            isDisabled={!canWriteRoles}
          >
            Create Role
          </Button>
        </Tooltip>
      </PageActions>
      <Loading isLoading={isInitialLoad}>
        <div className="px-[var(--page-margin)]">
          <DataTable<TableRow>
            {...tableDefaults}
            columns={columns}
            data={roles}
            isLoading={isLoading}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationLock />}
                title="No roles found"
                description="Create a custom role to get started."
                action={
                  <Tooltip
                    content={MANAGE_ROLES_PERMISSION_TOOLTIP}
                    isOpen={canWriteRoles ? false : undefined}
                    placement="bottom"
                  >
                    <Button
                      onClick={handleCreateRole}
                      icon={<Icon.Plus />}
                      size="small"
                      variant="secondary"
                      isDisabled={!canWriteRoles}
                    >
                      Create Role
                    </Button>
                  </Tooltip>
                }
              />
            }
            rowClassName={(row) =>
              row.pendingApproval ? 'group/row min-h-11' : row.editable ? 'min-h-11' : 'min-h-11 cursor-default'
            }
            rowKey={(row, index) => row.roleId ?? index}
            onRowClick={(row) => {
              if (row.pendingApproval) {
                openReview(row.pendingApproval);
              } else if (row.editable) {
                navigate(`${ROUTE.PLATFORM_ROLES}/${row.roleId}`);
              }
            }}
          />
        </div>
      </Loading>
    </>
  );
};
